import * as THREE from 'three'
import { WEBGL2_ONLY_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import type { EngineContext } from '@/runtime/EngineContext'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { AssetKind } from '@/shared/types/core'
import type { Plugin } from '@/shared/types/plugin'
import type {
  CameraClipBoundsContext,
  RenderContributor,
  RenderPipeline,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import { Box3Helper } from '@/shared/utils/Box3Helper'

/** 选中资产包围盒高亮线框颜色 */
const BBOX_HIGHLIGHT_COLOR = 0x0a65b9

export type BoundingBoxLineStyle = 'solid' | 'dashed'
export type BoundingBoxHighlightAssetKind = Extract<AssetKind, 'pointCloud' | 'gaussian-splat'>

export interface DynamicBoundingBoxHighlightEntry {
  /** 资产 id */
  id: string
  /** 资产类型，默认点云 */
  kind?: BoundingBoxHighlightAssetKind
  /** 包围盒线型，默认实线 */
  lineStyle?: BoundingBoxLineStyle
}

interface NormalizedDynamicBoundingBoxHighlightEntry {
  id: string
  kind: BoundingBoxHighlightAssetKind
  lineStyle: BoundingBoxLineStyle
}

const DEFAULT_DYNAMIC_HIGHLIGHT_KIND: BoundingBoxHighlightAssetKind = 'pointCloud'

/** 静态包围盒条目：未加载点云的世界 AABB（绝对坐标） */
export interface StaticBoundingBoxEntry {
  /** 业务唯一标识（通常为树节点 key） */
  id: string
  /** 世界坐标最小点 [x, y, z] */
  min: readonly [number, number, number]
  /** 世界坐标最大点 [x, y, z] */
  max: readonly [number, number, number]
  /** 包围盒线型，默认实线 */
  lineStyle?: BoundingBoxLineStyle
}

const BOX_EDGE_VERTEX_INDICES = [
  0, 1, 1, 2, 2, 3, 3, 0, 4, 5, 5, 6, 6, 7, 7, 4, 0, 4, 1, 5, 2, 6, 3, 7,
] as const

function isFiniteBox(box: THREE.Box3): boolean {
  const { min, max } = box
  return (
    Number.isFinite(min.x) &&
    Number.isFinite(min.y) &&
    Number.isFinite(min.z) &&
    Number.isFinite(max.x) &&
    Number.isFinite(max.y) &&
    Number.isFinite(max.z)
  )
}

/**
 * 点云 / 3DGS 包围盒高亮覆盖插件。
 *
 * 在 Overlay3D 阶段绘制包围盒线框，支持单/多选高亮，两类来源并存：
 * - 动态：已加载资产按 kind + id，每帧从 sceneBounds 重算（紧致 fit 盒，跟随移动/旋转）；
 * - 静态：未加载点云按外部给定的世界 AABB 固定显示（来自 metadata.json，无需加载 octree）。
 */
export class BoundingBoxOverlayPlugin implements Plugin, RenderContributor {
  /** 插件唯一名称 */
  readonly name = 'boundingBoxOverlay'
  readonly graphicsRequirements = WEBGL2_ONLY_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 56
  /** 依赖的服务名列表 */
  readonly dependencies = ['RenderPipeline'] as const

  /** 渲染贡献者类型标识 */
  readonly kind = 'bounding-box-overlay'
  /** 参与的渲染阶段 */
  readonly phases = [RenderPhase.Overlay3D] as const
  /** 渲染优先级 */
  readonly renderPriority = 0

  private context!: EngineContext
  private pipeline!: RenderPipeline

  private readonly overlayScene = new THREE.Scene()
  /** 已加载资产：按 kind + id，每帧从 sceneBounds 重算 */
  private readonly dynamicHelpers = new Map<string, Box3Helper>()
  /** 未加载点云：按外部 id，固定世界 AABB */
  private readonly staticHelpers = new Map<string, Box3Helper>()
  /** 未加载点云的静态世界范围，用于相机裁剪范围计算 */
  private readonly staticBounds = new Map<string, THREE.Box3>()
  private highlightedEntries: readonly NormalizedDynamicBoundingBoxHighlightEntry[] = []
  private readonly dynamicLineStyles = new Map<string, BoundingBoxLineStyle>()
  private syncedFrame = -1
  /** 世界包围盒转 helper 局部坐标时复用的计算缓存 */
  private readonly helperCenter = new THREE.Vector3()
  private readonly helperLocalBox = new THREE.Box3()

  /** 初始化插件，缓存 EngineContext */
  onInit(context: EngineContext): void {
    this.context = context
  }

  /** 启动插件，注册为渲染贡献者 */
  onStart(): void {
    this.pipeline = this.context.getService('RenderPipeline')
    this.pipeline.addRenderContributor(this)
  }

  /** 销毁插件，移除贡献者并释放线框资源 */
  onDestroy(): void {
    this.pipeline.removeRenderContributor(this)
    this.clearHelpers()
  }

  /**
   * 设置需要高亮包围盒的【已加载】资产 id 集合（动态，跟随位姿）。
   *
   * @param entries - 资产 id 或高亮条目数组；字符串兼容为点云；传空数组清除全部动态高亮
   */
  setHighlightedIds(entries: ReadonlyArray<string | DynamicBoundingBoxHighlightEntry>): void {
    this.dynamicLineStyles.clear()
    const normalizedEntries = new Map<string, NormalizedDynamicBoundingBoxHighlightEntry>()
    for (const entry of entries) {
      const normalized = this.normalizeDynamicEntry(entry)
      normalizedEntries.set(this.makeDynamicHelperKey(normalized.kind, normalized.id), normalized)
    }
    this.highlightedEntries = Array.from(normalizedEntries.values())
    for (const entry of this.highlightedEntries) {
      this.dynamicLineStyles.set(this.makeDynamicHelperKey(entry.kind, entry.id), entry.lineStyle)
    }
    this.syncedFrame = -1
    // 立即移除不再需要高亮的动态线框
    const highlightedHelperKeys = new Set(
      this.highlightedEntries.map((entry) => this.makeDynamicHelperKey(entry.kind, entry.id))
    )
    for (const [id, helper] of this.dynamicHelpers) {
      if (!highlightedHelperKeys.has(id)) {
        this.overlayScene.remove(helper)
        this.disposeHelper(helper)
        this.dynamicHelpers.delete(id)
      }
    }
  }

  /**
   * 设置【未加载】点云的静态包围盒（直接给世界 AABB，不经 sceneBounds 资产解析）。
   *
   * @param entries - 静态包围盒条目数组；传空数组清除全部静态盒
   */
  setStaticBoxes(entries: ReadonlyArray<StaticBoundingBoxEntry>): void {
    const incoming = new Set(entries.map((entry) => entry.id))
    for (const [id, helper] of this.staticHelpers) {
      if (!incoming.has(id)) {
        this.overlayScene.remove(helper)
        this.disposeHelper(helper)
        this.staticHelpers.delete(id)
        this.staticBounds.delete(id)
      }
    }
    for (const entry of entries) {
      const box = new THREE.Box3(
        new THREE.Vector3(entry.min[0], entry.min[1], entry.min[2]),
        new THREE.Vector3(entry.max[0], entry.max[1], entry.max[2])
      )
      if (box.isEmpty() || !isFiniteBox(box)) {
        const existing = this.staticHelpers.get(entry.id)
        if (existing) {
          this.overlayScene.remove(existing)
          this.disposeHelper(existing)
          this.staticHelpers.delete(entry.id)
        }
        this.staticBounds.delete(entry.id)
        continue
      }
      this.staticBounds.set(entry.id, box.clone())
      const existing = this.staticHelpers.get(entry.id)
      if (!existing) {
        const created = this.createBoundingBoxHelper(
          box,
          `staticBoundingBoxHelper:${entry.id}`,
          entry.lineStyle ?? 'solid'
        )
        this.staticHelpers.set(entry.id, created)
        this.overlayScene.add(created)
      } else {
        this.updateHelperGeometry(existing, box)
        this.applyHelperLineStyle(existing, entry.lineStyle ?? 'solid')
        existing.visible = true
      }
    }
  }

  /**
   * 返回当前高亮框需要纳入相机 near/far 计算的世界范围。
   *
   * @param _context - 当前视图上下文；高亮框当前对所有视图共享
   * @returns 动态与静态高亮框的世界包围盒并集
   */
  getCameraClipBounds(_context: CameraClipBoundsContext): THREE.Box3 {
    const result = new THREE.Box3()

    for (const entry of this.highlightedEntries) {
      const box = this.context.sceneBounds.getWorldBoundingBox({
        ids: [entry.id],
        kinds: [entry.kind],
        includeInvisible: true,
        purpose: 'fit',
      })
      if (!box.isEmpty() && isFiniteBox(box)) {
        result.union(box)
      }
    }

    for (const box of this.staticBounds.values()) {
      result.union(box)
    }

    return result
  }

  /**
   * 渲染包围盒覆盖层。
   *
   * @param frame - View 帧上下文
   * @param _phase - 当前渲染阶段，已由 phases 约束
   * @param ctx - 阶段渲染上下文
   */
  render(frame: ViewFrameContext, _phase: RenderPhase, ctx: PhaseRenderContext): void {
    // 离屏目标（如截图）不绘制高亮框
    if (ctx.outputTarget !== null) {
      return
    }
    if (
      this.highlightedEntries.length === 0 &&
      this.dynamicHelpers.size === 0 &&
      this.staticHelpers.size === 0
    ) {
      return
    }

    this.syncHelpers(frame.frameNumber)
    if (this.dynamicHelpers.size === 0 && this.staticHelpers.size === 0) {
      return
    }

    const { renderer } = ctx
    const savedTarget = renderer.getRenderTarget()
    const savedCameraLayerMask = frame.camera.layers.mask
    const savedViewport = renderer.getViewport(new THREE.Vector4())
    const savedScissor = renderer.getScissor(new THREE.Vector4())
    const savedScissorTest = renderer.getScissorTest()

    try {
      renderer.setRenderTarget(ctx.outputTarget)
      renderer.resetState()
      frame.camera.layers.mask = 0xffffffff
      renderer.setViewport(ctx.viewport.x, ctx.viewport.y, ctx.viewport.width, ctx.viewport.height)
      renderer.setScissor(ctx.viewport.x, ctx.viewport.y, ctx.viewport.width, ctx.viewport.height)
      renderer.setScissorTest(true)
      renderer.render(this.overlayScene, frame.camera)
    } finally {
      frame.camera.layers.mask = savedCameraLayerMask
      renderer.setRenderTarget(savedTarget)
      renderer.setViewport(savedViewport.x, savedViewport.y, savedViewport.z, savedViewport.w)
      renderer.setScissor(savedScissor.x, savedScissor.y, savedScissor.z, savedScissor.w)
      renderer.setScissorTest(savedScissorTest)
    }
  }

  /** 按当前帧重算各动态高亮资产的世界 AABB，并同步线框（每帧仅一次，避免多视图重复计算） */
  private syncHelpers(frameNumber: number): void {
    if (frameNumber === this.syncedFrame) {
      return
    }
    this.syncedFrame = frameNumber

    for (const entry of this.highlightedEntries) {
      const helperKey = this.makeDynamicHelperKey(entry.kind, entry.id)
      // purpose='fit'：取紧致包围盒，紧贴资产且跟随位姿
      const box = this.context.sceneBounds.getWorldBoundingBox({
        ids: [entry.id],
        kinds: [entry.kind],
        includeInvisible: true,
        purpose: 'fit',
      })
      const helper = this.dynamicHelpers.get(helperKey)
      if (!box || box.isEmpty()) {
        if (helper) {
          helper.visible = false
        }
        continue
      }
      if (!helper) {
        const created = this.createBoundingBoxHelper(
          box,
          `boundingBoxHelper:${helperKey}`,
          this.dynamicLineStyles.get(helperKey) ?? 'solid'
        )
        this.dynamicHelpers.set(helperKey, created)
        this.overlayScene.add(created)
      } else {
        this.updateHelperGeometry(helper, box)
        this.applyHelperLineStyle(helper, this.dynamicLineStyles.get(helperKey) ?? 'solid')
        helper.visible = true
      }
    }
  }

  private normalizeDynamicEntry(
    entry: string | DynamicBoundingBoxHighlightEntry
  ): NormalizedDynamicBoundingBoxHighlightEntry {
    if (typeof entry === 'string') {
      return {
        id: entry,
        kind: DEFAULT_DYNAMIC_HIGHLIGHT_KIND,
        lineStyle: 'solid',
      }
    }
    return {
      id: entry.id,
      kind: entry.kind ?? DEFAULT_DYNAMIC_HIGHLIGHT_KIND,
      lineStyle: entry.lineStyle ?? 'solid',
    }
  }

  private makeDynamicHelperKey(kind: BoundingBoxHighlightAssetKind, id: string): string {
    return `${kind}:${id}`
  }

  private createBoundingBoxHelper(
    box: THREE.Box3,
    name: string,
    lineStyle: BoundingBoxLineStyle
  ): Box3Helper {
    const localBox = this.resolveHelperLocalBox(box)
    const helper = new Box3Helper(localBox, BBOX_HIGHLIGHT_COLOR)
    helper.position.copy(this.helperCenter)
    helper.name = name
    helper.frustumCulled = false
    this.applyHelperLineStyle(helper, lineStyle)
    return helper
  }

  /** 将世界包围盒拆为双精度世界中心和 Float32 可安全表达的局部范围。 */
  private resolveHelperLocalBox(box: THREE.Box3): THREE.Box3 {
    box.getCenter(this.helperCenter)
    this.helperLocalBox.min.subVectors(box.min, this.helperCenter)
    this.helperLocalBox.max.subVectors(box.max, this.helperCenter)
    return this.helperLocalBox
  }

  private getDashSize(helper: Box3Helper): number {
    helper.geometry.computeBoundingSphere()
    const radius = helper.geometry.boundingSphere?.radius
    if (!Number.isFinite(radius) || !radius || radius <= 0) {
      return 1
    }
    return Math.max(radius / 40, 0.1)
  }

  private replaceHelperMaterial(
    helper: Box3Helper,
    material: THREE.LineBasicMaterial | THREE.LineDashedMaterial
  ): void {
    const oldMaterial = helper.material
    helper.material = material
    if (Array.isArray(oldMaterial)) {
      oldMaterial.forEach((m) => m.dispose())
    } else {
      oldMaterial.dispose()
    }
  }

  private applyHelperLineStyle(helper: Box3Helper, lineStyle: BoundingBoxLineStyle): void {
    if (lineStyle === 'dashed') {
      const dashSize = this.getDashSize(helper)
      this.ensureNonIndexedLineSegments(helper)
      if (!(helper.material instanceof THREE.LineDashedMaterial)) {
        this.replaceHelperMaterial(
          helper,
          new THREE.LineDashedMaterial({
            color: BBOX_HIGHLIGHT_COLOR,
            dashSize,
            gapSize: dashSize * 0.75,
          })
        )
      } else {
        helper.material.dashSize = dashSize
        helper.material.gapSize = dashSize * 0.75
      }
      helper.computeLineDistances()
      return
    }

    if (
      helper.material instanceof THREE.LineDashedMaterial ||
      !(helper.material instanceof THREE.LineBasicMaterial)
    ) {
      this.replaceHelperMaterial(
        helper,
        new THREE.LineBasicMaterial({
          color: BBOX_HIGHLIGHT_COLOR,
        })
      )
    }
  }

  private ensureNonIndexedLineSegments(helper: Box3Helper): void {
    const position = helper.geometry.getAttribute('position') as THREE.BufferAttribute | undefined
    if (!helper.geometry.index && position?.count === BOX_EDGE_VERTEX_INDICES.length) {
      return
    }

    const box = new THREE.Box3().setFromBufferAttribute(position as THREE.BufferAttribute)
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(this.createLineSegmentPositions(box), 3)
    )
    helper.geometry.dispose()
    helper.geometry = geometry
  }

  private createLineSegmentPositions(box: THREE.Box3): Float32Array {
    const { min, max } = box
    const vertices = [
      [min.x, min.y, min.z],
      [max.x, min.y, min.z],
      [max.x, min.y, max.z],
      [min.x, min.y, max.z],
      [min.x, max.y, min.z],
      [max.x, max.y, min.z],
      [max.x, max.y, max.z],
      [min.x, max.y, max.z],
    ] as const
    const positions = new Float32Array(BOX_EDGE_VERTEX_INDICES.length * 3)
    BOX_EDGE_VERTEX_INDICES.forEach((vertexIndex, index) => {
      const vertex = vertices[vertexIndex]
      positions[index * 3] = vertex[0]
      positions[index * 3 + 1] = vertex[1]
      positions[index * 3 + 2] = vertex[2]
    })
    return positions
  }

  /** 用最新世界 AABB 更新 helper 世界中心和局部顶点（顺序与 Box3Helper 构造一致） */
  private updateHelperGeometry(helper: Box3Helper, box: THREE.Box3): void {
    const localBox = this.resolveHelperLocalBox(box)
    helper.position.copy(this.helperCenter)
    const position = helper.geometry.getAttribute('position') as THREE.BufferAttribute
    if (!helper.geometry.index && position.count === BOX_EDGE_VERTEX_INDICES.length) {
      position.copyArray(this.createLineSegmentPositions(localBox))
      position.needsUpdate = true
      helper.geometry.computeBoundingSphere()
      return
    }

    const { min, max } = localBox
    position.setXYZ(0, min.x, min.y, min.z)
    position.setXYZ(1, max.x, min.y, min.z)
    position.setXYZ(2, max.x, min.y, max.z)
    position.setXYZ(3, min.x, min.y, max.z)
    position.setXYZ(4, min.x, max.y, min.z)
    position.setXYZ(5, max.x, max.y, min.z)
    position.setXYZ(6, max.x, max.y, max.z)
    position.setXYZ(7, min.x, max.y, max.z)
    position.needsUpdate = true
    helper.geometry.computeBoundingSphere()
  }

  /** 释放所有线框资源 */
  private clearHelpers(): void {
    for (const [, helper] of this.dynamicHelpers) {
      this.overlayScene.remove(helper)
      this.disposeHelper(helper)
    }
    this.dynamicHelpers.clear()
    for (const [, helper] of this.staticHelpers) {
      this.overlayScene.remove(helper)
      this.disposeHelper(helper)
    }
    this.staticHelpers.clear()
    this.staticBounds.clear()
    this.highlightedEntries = []
    this.dynamicLineStyles.clear()
    this.syncedFrame = -1
  }

  /** 释放单个线框的几何与材质 */
  private disposeHelper(helper: Box3Helper): void {
    helper.geometry.dispose()
    const material = helper.material
    if (Array.isArray(material)) {
      material.forEach((m) => m.dispose())
    } else {
      material.dispose()
    }
  }
}
